/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: package-media-service.ts
 *
 * Created by randysorensen on 9/10/18.
 */

//
// NOTICE:
//
// 2018-Sep-19 - Robert is no longer using package artwork and
// permitted us to remove it from the package media tab.
//


angular.module('prism-ui.package').service('PackageMediaService', function (
    this: JS5Object,
    _: JS5Object,
    $q: JS5Object,
    AsperaConnectService: JS5Object,
    ContentResourceFactory: JS5Object,
    LanguageResourceFactory: JS5Object,
    ModalService: JS5Object,
    PackageFileUploadService: JS5Object,
    PackageMediaResourceFactory: JS5Object,
    PackageResourceFactory: JS5Object,
    PackageUtilService: JS5Object,
    PermissionsService: JS5Object,
    RequestService: JS5Object,
    RolesService: JS5Object
) {
    // private members
    const viewAssetModalRendererUrl = 'package/editor/components/view/media/renderers/packagemedia-view-asset-mediainfo-template.html';

    let packageMediaDataDeferred: JS5Object = null;
    let ingestPollingIntervalId: number = null;

    this.resetPackageMediaData = (packageObject: JS5Object) => {
        this.deactivateIngestProgressPollingInterval();

        PackageFileUploadService.unregisterAllFiles();

        packageMediaDataDeferred = $q.defer();
        this.packageObject = packageObject;

        if (!this.packageMediaPermissions) {
            this.packageMediaPermissions = {
                isAdmin: false,
                canRead: false,
                canWrite: false
            };
        }

        this.originalPackageMediaData = null;

        this.packageMediaData = {
            languages: [],

            componentRoles: [],
            audioRoles: [],
            ccRoles: [],
            subtitleRoles: [],

            requirements: [],
            mainVideoMediaInfoXML: null,

            selectedImageAcceptTemplateMember: _.get(this, `packageObject.primaryImage.acceptTemplateMember`, null),
            selectedVideoAcceptTemplateMember: _.get(this, `packageObject.primaryAcceptTemplateMember`, null),
            adPodAcceptProfile: null,

            acceptTemplateMembers: [],
            assets: [],

            //
            // We set the pending ingest flag to whatever the
            // value on the package is. But we do not bind
            // anything in the UI to it; it will be set or
            // cleared by the media tab controller based on
            // the user's modal response, and then passed as
            // a query parameter to the package media API.
            //
            // The server will then set/clear the flag on the
            // package as needed.
            //

            pendingIngest: _.get(this, `packageObject.pendingIngest`, true),
        };
    };


    //=================//
    // Validation Type //
    //=================//

    const supportedAssetTypes = ['IMAGE', 'AUDIO', 'OTHER', 'DOCUMENT'];
    const supportedTextTrackTypes = ['CC', 'SUBTITLE'];
    const alwaysRequiredTypes = ['SDR VIDEO', 'HDR VIDEO', 'VIDEO METADATA', 'AD POD'];

    const getPackageValidationStatusForComponentRole = (componentRole: JS5Object): string => {
        let validationStatus = 'OPTIONAL';
        if (componentRole.isDefault || componentRole._partnersRequiring.length) {
            validationStatus = 'REQUIRED';
        }
        return validationStatus;
    };

    const getPackageValidationStatusForNonTextTrackAssets = (mediaType: string): string => {
        let acceptTemplateMembers: JS5Object[] = _.filter(this.packageMediaData.acceptTemplateMembers, {
            acceptProfile: {
                type: mediaType
            }
        });
        let hasRequired = _.some(acceptTemplateMembers, {validationType: 'REQUIRED'});
        let hasOptional = _.some(acceptTemplateMembers, {validationType: 'OPTIONAL'});

        let validationStatus = 'NOT APPLICABLE';
        if (hasRequired) {
            validationStatus = 'REQUIRED';
        } else if (hasOptional) {
            validationStatus = 'OPTIONAL';
        }
        return validationStatus;
    };

    const getPackageValidationStatusForTextTrackAssets = (variant: string, componentRole: JS5Object): string => {
        variant = _.toLower(variant);

        let validationStatus = 'NOT APPLICABLE';
        if (_.get(componentRole, `_${variant}AcceptTemplate`)) {
            validationStatus = 'REQUIRED';

            const componentRoleValidationStatus = getPackageValidationStatusForComponentRole(componentRole);
            const componentRoleIsRequired = _.includes(componentRoleValidationStatus, 'REQUIRED');
            const extractClosedCaptioning = _.get(this.getPrimaryAcceptTemplateMember(), 'acceptProfile.extractClosedCaptioning');

            if (!componentRole.isDefault && !componentRoleIsRequired) {
                validationStatus += ' (If Video Ingested)';
            } else if (extractClosedCaptioning && variant === 'cc' && componentRole.isDefault) {
                validationStatus += ' (Extracted from Video)';
            }
        } else if (_.get(componentRole, 'isDefault')) {
            validationStatus = 'OPTIONAL'
        }

        return validationStatus;
    };

    const getPackageValidationStatus = (mediaType: string, componentRole: JS5Object=null): string => {
        mediaType = _.toUpper(mediaType);
        if (!componentRole) {
            componentRole = this.getDefaultComponentRole();
        }

        let validationStatus = 'NOT APPLICABLE';
        if (mediaType === 'VIDEO') {
            validationStatus = getPackageValidationStatusForComponentRole(componentRole);
        } else if (_.includes(alwaysRequiredTypes, mediaType)) {
            validationStatus = 'REQUIRED';
        } else if (_.includes(supportedAssetTypes, mediaType)) {
            validationStatus = getPackageValidationStatusForNonTextTrackAssets(mediaType);
        } else if (_.includes(supportedTextTrackTypes, mediaType)) {
            validationStatus = getPackageValidationStatusForTextTrackAssets(mediaType, componentRole);
        }

        return validationStatus;
    };

    const getPartnerValidationStatus = (mediaType: string, componentRole: JS5Object): string => {
        mediaType = _.toLower(mediaType);
        let filterCriteria: JS5Object = {
            mediaType,
            required: true
        };
        if (componentRole) {
            filterCriteria.videoRoleId = componentRole.id;
        }

        let partnerRequirements = _.filter(this.packageMediaData.requirements, filterCriteria);

        let validationStatus = 'NOT APPLICABLE';
        if (_.chain(partnerRequirements).map('partnerName').uniq().compact().size().value()) {
            validationStatus = 'REQUIRED (PARTNER)';
        }
        return validationStatus;
    };

    const getValidationStatusWeight = (validationStatus: string): number => {
        let weight = 0;
        if (_.includes(validationStatus, 'REQUIRED')) {
            weight += 2;
        } else if (_.includes(validationStatus, 'OPTIONAL')) {
            weight += 1;
        }
        return weight;
    };

    this.getValidationStatus = (mediaType: string, componentRole: JS5Object=null): string => {
        let packageValidationStatus = getPackageValidationStatus(mediaType, componentRole);
        let partnerValidationStatus = getPartnerValidationStatus(mediaType, componentRole);

        let packageValidationWeight = getValidationStatusWeight(packageValidationStatus);
        let partnerValidationWeight = getValidationStatusWeight(partnerValidationStatus);

        if (packageValidationWeight >= partnerValidationWeight) {
            return packageValidationStatus;
        } else {
            return partnerValidationStatus;
        }
    };

    this.getPartnersRequiring = (mediaType: string, componentRole: JS5Object=null): string[] => {
        mediaType = _.toLower(mediaType);

        let partnersRequiring: string[] = [];

        if (mediaType === 'video') {
            partnersRequiring = _.get(componentRole, '_partnersRequiring', []);
        } else {
            let filterCriteria: JS5Object = {
                mediaType,
                required: true
            };
            if (componentRole) {
                filterCriteria.videoRoleId = componentRole.id;
            }

            let partnerRequirements = _.filter(this.packageMediaData.requirements, filterCriteria);
            partnersRequiring = _.chain(partnerRequirements).map('partnerName').uniq().compact().value();
        }

        return partnersRequiring;
    };


    //=============//
    // Permissions //
    //=============//

    const MEDIA_TAB_VIEW_STATE = 'PACKAGE_MEDIA_VIEW_STATE';

    this.getPackageMediaPermissions = () => {
        let adminFlag = PermissionsService.isAdmin();

        this.packageMediaPermissions.isAdmin = adminFlag;
        this.packageMediaPermissions.canRead = adminFlag;
        this.packageMediaPermissions.canWrite = adminFlag;

        if (!adminFlag) {
            return $q.all({
                canRead: PermissionsService.canRead(MEDIA_TAB_VIEW_STATE),
                canWrite: PermissionsService.canWrite(MEDIA_TAB_VIEW_STATE)
            }).then((permissionsData: JS5Object) => {
                this.packageMediaPermissions.canWrite = permissionsData.canWrite;
                this.packageMediaPermissions.canRead = permissionsData.canWrite || permissionsData.canRead;

                return this.packageMediaPermissions;
            });
        } else {
            return $q.when(this.packageMediaPermissions);
        }
    };


    //=====================//
    // Asset Editor Modals //
    //=====================//

    this.generatePackageDownloadFilenamePrefix = (packageObject: JS5Object,
                                                 secondaryAsset: boolean,
                                                 componentRoleName: string=null) => {
        let packageDownloadFilenamePrefix = (
            `${packageObject.content.prefix}` +
            `_${packageObject.productionNumber}` +
            `_${packageObject.title}` +
            `_${packageObject.id}` +
            `_`
        );

        if (componentRoleName !== null) {
            packageDownloadFilenamePrefix += `${componentRoleName}_`;
        }

        if (secondaryAsset) {
            packageDownloadFilenamePrefix += 'secondary_';
        }
        return packageDownloadFilenamePrefix;
    };

    this.openViewAssetModal = (packageContent: JS5Object, secondaryAsset: boolean=false) => {
        const ingestProgress: string = _.get(packageContent, `ingestInfo.progress`, 0);
        if (!!packageContent && parseInt(ingestProgress) === 100) {
            let fileNamePrefix = !!this.packageObject
                ? this.generatePackageDownloadFilenamePrefix(this.packageObject, secondaryAsset)
                : 'unknown_';
            ModalService.showViewAssetModal(packageContent, secondaryAsset, fileNamePrefix, viewAssetModalRendererUrl,
                true);
        }
    };

    this.openQualityControlModal = (asset: JS5Object) => {
        const setQCNote = (asset: JS5Object, qcNote: string) => {
            asset.qualityControlNote = qcNote;
            PackageUtilService.editorForm.$setDirty();
        };

        if (asset && PackageUtilService.statusAllowsEdit(this.packageObject.status)) {
            ModalService.showEditableQualityControlModal(asset, this.packageMediaPermissions.canWrite, setQCNote);
        }
    };


    //==================//
    // Media Operations //
    //==================//

    this.resetAllMedia = () => {
        this.deactivateIngestProgressPollingInterval();
        this.packageMediaData.assets = [];
        this.packageMediaData.selectedImageAcceptTemplateMember = null;
        this.packageMediaData.selectedVideoAcceptTemplateMember = null;
        PackageFileUploadService.unregisterAllFiles();
    };

    const determineSelectedImageAcceptTemplateMember = () => {
        let imageAssets = this.getAssetList('Image');
        if (_.size(imageAssets)) {
            let currentMAPs = _.chain(imageAssets).map('acceptTemplateMember').uniqBy('id').compact().value();
            if (_.size(currentMAPs) === 1) {
                this.packageMediaData.selectedImageAcceptTemplateMember = _.first(currentMAPs);
            } else {
                console.warn(`determineSelectedImageAcceptTemplateMember(): found multiple image MAPs`);
            }
        }
    };

    const processPackageMediaList = (packageMediaAssets: JS5Object[]) => {
        this.resetAllMedia();
        this.packageMediaData.assets = _.cloneDeep(packageMediaAssets);
        determineSelectedImageAcceptTemplateMember();
        let primaryAcceptTemplateMember = _.get(this.getMainVideo(), `acceptTemplateMember`);
        this.packageObject.primaryAcceptTemplateMember = primaryAcceptTemplateMember;
        this.packageMediaData.selectedVideoAcceptTemplateMember = primaryAcceptTemplateMember;
    };

    this.getAssetList = (type: string, componentRole: JS5Object=null) => {
        let filter = {
            type: type
        };

        if (componentRole) {
            if (type === 'Video') {
                _.set(filter, 'role.id', componentRole.id);
            } else {
                _.set(filter, 'componentRole.id', componentRole.id);
            }
        }

        return _.filter(this.packageMediaData.assets, filter);
    };

    this.getMainVideo = (): JS5Object => {
        let mainVideo = _.find(this.packageMediaData.assets, {
            type: 'Video',
            role: {
                isDefault: true
            }
        });

        if (!mainVideo) {
            mainVideo = {
                acceptTemplateMember: null,
                pendingIngest: false,
                role: _.find(
                    this.packageMediaData.componentRoles,
                    {
                        isDefault: true
                    }),
                type: 'Video',
                virtualPath: null
            };

            this.packageMediaData.assets.push(mainVideo);
        }

        return mainVideo;
    };

    this.mainVideoIngestHasCompleted = () => {
        const hasMainVideoID: boolean = !!this.getMainVideo().id;

        const ingestProgress: number = parseInt(_.get(this.getMainVideo(), 'ingestInfo.progress', 0));
        const ingestHasCompleted: boolean = ingestProgress === 100;

        return hasMainVideoID && ingestHasCompleted;
    };

    this.mainVideoIngestHasStarted = () => {
        const hasMainVideoID: boolean = !!this.getMainVideo().id;

        const ingestProgress: number = _.get(this.getMainVideo(), 'ingestInfo.progress', 0);
        const ingestWorkflowId: string = _.get(this.getMainVideo(), 'ingestInfo.workflowId', null);
        const proxyWorkflowId: string = _.get(this.getMainVideo(), 'ingestInfo.proxyWorkflowId', null);
        const ingestHasStarted: boolean = (ingestProgress > 0) || !!ingestWorkflowId || !!proxyWorkflowId;

        return hasMainVideoID && ingestHasStarted;
    };

    this.getPrimaryAcceptTemplateMember = () => {
        return (
            _.get(this, 'packageMediaData.selectedVideoAcceptTemplateMember') ||
            _.get(this.getMainVideo(), 'acceptTemplateMember') ||
            _.get(this, 'packageObject.primaryAcceptTemplateMember')
        );
    };

    this.setMainVideoMediaInfoXML = (mainVideoMediaInfoXML: string) => {
        console.info(`PackageMediaService.setMainVideoMediaInfoXML():` +
            ` resetting active main video Mediainfo XML`);
        this.packageMediaData.mainVideoMediaInfoXML = _.cloneDeep(mainVideoMediaInfoXML);
    };

    this.getMainVideoMediaInfoXML = () => {
        return this.packageMediaData.mainVideoMediaInfoXML;
    };

    this.getComponentVideoMediaInfoXMLList = (componentRole: JS5Object) => {
        let componentVideosForRole = this.getAssetList('Video', componentRole);
        return _.chain(componentVideosForRole).map('mediaInfoDTO').compact().value();
    };

    this.getDefaultComponentRole = () => {
        return _.chain(this.packageMediaData.componentRoles).filter({isDefault: true}).first().value();
    };

    this.hasMainVideo = () => {
        return !!_.get(this.getMainVideo(), 'virtualPath');
    };

    this.getExcludedComponentIdList = (role: JS5Object) => {
        let originalComponentList = _.filter(
            this.originalPackageMediaData.assets,
            {
                type: 'Video',
                role: {
                    id: role.id
                }
            });
        let originalComponentIdList = _.map(originalComponentList, 'id');

        let currentComponentList = this.getAssetList('Video', role);
        let currentComponentIdList = _.map(currentComponentList, 'id');

        return _.difference(originalComponentIdList, currentComponentIdList);
    };

    this.getNewComponentVirtualPathList = (role: JS5Object) => {
        let currentComponentList = this.getAssetList('Video', role);
        let newComponentList = _.filter(currentComponentList, (component: JS5Object) => {
            return !_.has(component, 'id');
        });

        return _.map(newComponentList, 'virtualPath');
    };

    const addRelatedItem = (relatedItem: JS5Object) => {
        const type = relatedItem.type.toLowerCase();
        const defaultRole = _.find(this.packageMediaData.componentRoles, {isDefault: true});
        const validationStatus = this.getValidationStatus(type, defaultRole);

        if (!_.includes(validationStatus, 'NOT APPLICABLE')) {
            if (type === 'cc' || type === 'subtitle') {
                relatedItem.componentRole = defaultRole;
            }
            this.packageMediaData.assets.push(relatedItem);
        } else {
            console.info(`addRelatedItem(): ignoring inapplicable related item ${relatedItem.virtualPath}`);
        }
    };

    this.findRelatedItems = () => {
        let acceptTemplateMemberId = _.get(this.getPrimaryAcceptTemplateMember(), 'id');

        let findRelatedPromise = PackageResourceFactory.findRelatedItems({
            id: this.packageObject.id,
            videoPath: _.get(this.getMainVideo(), 'virtualPath'),
            acceptTemplateMemberId: acceptTemplateMemberId
        }).$promise;

        return findRelatedPromise.then((relatedItems: JS5Object[]) => {
            let validationResults: JS5Object[] = [];
            let relatedItemsToConfirm: JS5Object[] = [];

            _.forEach(relatedItems, (relatedItem: JS5Object) => {
                if (_.get(relatedItem, 'validationResult', []).length) {
                    validationResults = _.concat(validationResults, relatedItem.validationResult);
                    relatedItemsToConfirm.push(relatedItem);
                } else {
                    addRelatedItem(relatedItem);
                }
            });

            if (_.size(validationResults)) {
                ModalService.showAssetValidationResultsModal(validationResults, true, () => {
                    _.forEach(relatedItemsToConfirm, addRelatedItem);
                });
            }

            determineSelectedImageAcceptTemplateMember();
        });
    };

    this.isAnUploadPath = (virtualPath: string=null) => {
        if (!virtualPath) {
            virtualPath = _.get(this.getMainVideo(), 'virtualPath');
        }
        const asperaVirtualRoot = _.get(AsperaConnectService, 'asperaConnectProperties.virtualRoot', null);

        return _.startsWith(virtualPath, asperaVirtualRoot) || _.startsWith(virtualPath, 's3:');
    };


    //=================//
    // Ingest Progress //
    //=================//

    this.calculateIngestPercent = (assetList: JS5Object[]): number => {
        let assetCount: number = _.size(assetList);
        let percentSum: number = 0;

        _.forEach(assetList, (asset: JS5Object) => {
            if (_.has(asset, 'ingestInfo')) {
                percentSum += _.chain(asset).get('ingestInfo.progress', 0).toInteger().value();
            } else {
                --assetCount;
            }
        });

        let ingestPercent: number;
        if (assetCount === 0) {
            ingestPercent = _.toInteger(-1);
        } else {
            ingestPercent = _.toInteger(percentSum / assetCount);
        }
        return ingestPercent;
    };

    this.deactivateIngestProgressPollingInterval = () => {
        RequestService.cancelRequestInterval(ingestPollingIntervalId);
    };

    this.activateIngestProgressPollingInterval = () => {
        this.deactivateIngestProgressPollingInterval();
        ingestPollingIntervalId = RequestService.createRequestInterval(this.updateIngestStatus, 10000, {startImmediately: true});
    };

    this.restartIngestProgressPollingInterval = () => {
        return this.activateIngestProgressPollingInterval();
    };


    //=========================//
    // Retrieve Media Tab Data //
    //=========================//

    const getLanguages = () => {
        return LanguageResourceFactory.query().$promise.then((languages: JS5Object[]) => {
            this.packageMediaData.languages =
                _.chain(languages).orderBy('name').partition({isDefault: true}).flatten().concat().value();
        })
    };

    const getRoles = () => {
        return RolesService.getAllRoles().then((roles: JS5Object[]) => {
            _.forEach(roles, (role: JS5Object) => {
                switch (role.type.toLowerCase()) {
                    case 'video': this.packageMediaData.componentRoles.push(role); break;
                    case 'audio': this.packageMediaData.audioRoles.push(role); break;
                    case 'cc': this.packageMediaData.ccRoles.push(role); break;
                    case 'subtitle': this.packageMediaData.subtitleRoles.push(role); break;
                    default: console.warn(`Unknown role type: ${role.type}`); break;
                }
            });
        });
    };

    const getPackageAcceptTemplateMembers = () => {

        //
        // Note that we ignore the 'document' accept profile type,
        // as they are only used for Ad Pods, and  the correct
        // Ad Pod accept profile is specified by the package
        // category and may not be changed on the package Media
        // tab
        //

        return PackageResourceFactory.query({
            id: this.packageObject.id,
            sub: 'acceptTemplateMembers'
        }).$promise.then((acceptTemplateMembers: JS5Object[]) => {
            this.packageMediaData.acceptTemplateMembers = _.cloneDeep(acceptTemplateMembers);
        });
    };

    const getPackageMediaRequirements = () => {

        //
        // The package media requirements should always be
        // updateable, however there are no requirements unless
        // a package video MAP is active.
        //

        let requirementsDeferred = $q.defer();
        const acceptTemplateMember = _.get(this, `packageObject.primaryAcceptTemplateMember`);
        if (_.has(acceptTemplateMember, `acceptProfile.id`)) {
            requirementsDeferred.resolve(PackageResourceFactory.query({
                id: this.packageObject.id,
                mapId: acceptTemplateMember.acceptProfile.id,
                sub: 'mediaRequirement'
            }).$promise.then((packageMediaRequirements: JS5Object[]) => {
                _.forEach(packageMediaRequirements, (packageMediaRequirement: JS5Object) => {
                    let mediaType: string = packageMediaRequirement.mediaType;
                    if (mediaType === 'PACKAGE_IMAGE') {
                        mediaType = 'image';
                    } else {
                        mediaType = _.toLower(mediaType);
                    }
                    packageMediaRequirement.mediaType = mediaType;
                });
                this.packageMediaData.requirements = packageMediaRequirements;
            }));
        } else {
            this.packageMediaData.requirements = [];
            requirementsDeferred.resolve();
        }
        return requirementsDeferred.promise;
    };

    const getPackageMediaAssets = () => {
        return PackageMediaResourceFactory.get({
            id: this.packageObject.id
        }).$promise.then(processPackageMediaList);
    };

    const getContentCategory = () => {
        return ContentResourceFactory.query({
            id: this.packageObject.content.id,
            sub: 'categories',
            categoryId: this.packageObject.category.id
        }).$promise.then((contentCategories: JS5Object[]) => {
            // server should only return one content category when
            // a category ID is provided
            if (contentCategories.length !== 1) {
                console.warn(`Expected exactly 1 content category (found ${contentCategories.length})`);
            }
            let contentCategory = contentCategories[0];

            // save Ad Pod accept profile from category
            this.packageMediaData.adPodAcceptProfile = _.get(contentCategory, 'category.adPodAcceptProfile');

            // save component roles and associated text accept
            // templates - note that each element in the videoRoles
            // array from the server has text accept templates as
            // well as a video role object named simply "role"
            _.forEach(_.get(contentCategory, 'videoRoles'), (videoRole: JS5Object) => {
                let componentRole = _.find(
                    this.packageMediaData.componentRoles,
                    {
                        id: videoRole.role.id
                    });
                componentRole._ccAcceptTemplate = videoRole.closedCaptioningAcceptTemplate;
                componentRole._subtitleAcceptTemplate = videoRole.subtitleAcceptTemplate;

                if (!videoRole.role.isDefault) {
                    componentRole._contentCategoryEnabled = true;
                    componentRole._partnersRequiring = [];
                    componentRole._partnersEnabled = [];
                }
            });
        });
    };

    const getPartnerPackages = () => {
        return PackageResourceFactory.query({
            id: this.packageObject.id,
            sub: 'partnerPackages'
        }).$promise.then((partnerPackages: JS5Object[]) => {
            _.forEach(partnerPackages, (partnerPackage: JS5Object) => {
                const requiredComponentRoleIds =
                    _.chain(partnerPackage.videoRoles).filter({isRequired: true}).map('role.id').value();
                const optionalComponentRoleIds =
                    _.chain(partnerPackage.videoRoles).filter({isRequired: false}).map('role.id').value();

                _.forEach(requiredComponentRoleIds, (roleId: string) => {
                    let role = _.find(this.packageMediaData.componentRoles, {id: roleId});
                    role._partnersRequiring.push(partnerPackage.partner.name);
                });
                _.forEach(optionalComponentRoleIds, (roleId: string) => {
                    let role = _.find(this.packageMediaData.componentRoles, {id: roleId});
                    role._partnersEnabled.push(partnerPackage.partner.name);
                });
            });
        });
    };

    this.updateIngestStatus = () => {
        return PackageMediaResourceFactory.get({
            id: this.packageObject.id
        }).$promise.then((packageMediaAssets: JS5Object[]) => {
            _.forEach(packageMediaAssets, (packageMediaAsset: JS5Object) => {
                let localAsset: JS5Object = _.find(this.packageMediaData.assets, {id: packageMediaAsset.id});
                if (localAsset) {
                    if (_.has(packageMediaAsset, 'ingestInfo')) {
                        localAsset.ingestInfo = packageMediaAsset.ingestInfo;
                    } else {
                        delete localAsset.ingestInfo;
                    }
                }
            });
        }).then(() => {
            getPackageMediaRequirements();
        });
    };

    this.getPackageMediaData = (packageObject: JS5Object) => {
        if (_.get(this, 'packageObject.id') === _.get(packageObject, 'id')) {

            //
            // We already have this package's media data, so just
            // detach the old package object and attach the new
            // one
            //

            this.packageObject = packageObject;
            return packageMediaDataDeferred.promise;
        } else if (packageObject !== undefined) {
            this.resetPackageMediaData(packageObject);

            if (_.has(packageObject, 'category.id')) {
                $q.all([
                    getLanguages(),
                    getRoles(),
                    getPackageAcceptTemplateMembers(),
                    getPackageMediaRequirements()
                ]).then(() => {
                    return getContentCategory();
                }).then(() => {
                    return getPartnerPackages();
                }).then(() => {
                    return getPackageMediaAssets();
                }).then(() => {
                    this.packageObject.primaryAcceptTemplateMember =
                        _.get(this.getMainVideo(), `acceptTemplateMember`, null);
                    this.originalPackageMediaData = _.cloneDeep(this.packageMediaData);
                    packageMediaDataDeferred.resolve(this.packageMediaData);
                });
            } else {
                this.originalPackageMediaData = _.cloneDeep(this.packageMediaData);
                packageMediaDataDeferred.resolve(this.packageMediaData);
            }
        }

        return packageMediaDataDeferred.promise;
    };


    //=============//
    // Save/Revert //
    //=============//

    const textTrackListHasDuplicates = (textTrackList: JS5Object[]) => {
        let duplicatesFound = false;

        _.forEach(textTrackList, (textAsset: JS5Object) => {
            if (!duplicatesFound) {
                let filterCriteria = {
                    language: {
                        id: _.get(textAsset, 'language.id')
                    },
                    role: {
                        id: _.get(textAsset, 'role.id')
                    }
                };
                duplicatesFound = _.filter(textTrackList, filterCriteria).length > 1;
            }
        });

        return duplicatesFound;
    };

    const componentRoleHasDuplicateTextLanguageAndRole = (textAssetType: string, componentRole: JS5Object) => {
        let assetList = _.filter(
            this.packageMediaData.assets,
            {
                type: textAssetType,
                componentRole: {
                    id: componentRole.id
                }
            });

        return _.size(assetList) >= 2 && textTrackListHasDuplicates(assetList);
    };

    const hasDuplicateTextLanguageAndRole = () => {
        let duplicatesFound = false;

        _.forEach(this.packageMediaData.componentRoles, (componentRole: JS5Object) => {
            if (!duplicatesFound) {
                duplicatesFound =
                    componentRoleHasDuplicateTextLanguageAndRole('CC', componentRole) ||
                    componentRoleHasDuplicateTextLanguageAndRole('Subtitle', componentRole);
            }
        });

        return duplicatesFound;
    };

    this.hasSaveConflict = () => {
        return hasDuplicateTextLanguageAndRole();
    };

    this.savePackageMediaData = () => {
        this.deactivateIngestProgressPollingInterval();

        let savePromise = PackageMediaResourceFactory.save({
            id: this.packageObject.id,
            pendingIngest: this.packageMediaData.pendingIngest
        }, this.packageMediaData.assets).$promise;

        return savePromise.then((savedMediaAssets: JS5Object[]) => {
            processPackageMediaList(savedMediaAssets);
        }).then(() => {
            this.originalPackageMediaData = _.cloneDeep(this.packageMediaData);
        }).finally(() => {
            return this.activateIngestProgressPollingInterval();
        });
    };

    this.revertPackageMediaData = () => {
        //
        // To prevent ingest indicator flickering after a revert,
        // we remove ingest info from all assets and update it
        // immediately after reverting.
        //

        this.deactivateIngestProgressPollingInterval();
        _.forEach(this.originalPackageMediaData.assets, (asset: JS5Object) => {
            delete asset.ingestInfo;
        });
        this.packageMediaData = _.cloneDeep(this.originalPackageMediaData);
        return this.activateIngestProgressPollingInterval();
    };

    //===============//
    // Service Entry //
    //===============//

    const svcInit = () => {
        this.packageMediaDataPromise = null;
        this.resetPackageMediaData(null);
    };

    svcInit();
});
